import React, { useEffect, useState, useContext } from 'react';
import { useMutation } from '@apollo/client';
import { AuthContext } from '@xometry/auth-context';

import {
  Alert,
  Box,
  Button,
  Flex,
  FormMessage,
  Input,
  Logo,
  Spinner,
  Stepper,
  Text,
  theme,
} from '@xometry/xometry_loft';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import * as yup from 'yup';
import { Formik } from 'formik';
import TooltipBlueIcon from '../../../components/TooltipBlueIcon';

import {
  CreateOrganizationMutationResult,
  CreateOrganizationMutationVariables,
  CreateOrganizationQuery,
  SendOrganizationInvitationsMutationResult,
  SendOrganizationInvitationsMutationVariables,
  SendOrganizationInvitationsQuery,
} from '../../../schema/generated/schema';

import authService from '../../../services/auth';
import quotesApi from '../../../services/quotes';
import EmailSelectCreatable from '../../../components/EmailSelectCreatable';

const CreateOrganization = () => {
  const [validatedTeamName, setValidatedTeamName] = useState('');
  const [invitationEmailList, setInvitationEmailList] = useState([]);
  const [isValidInvitationList, setIsValidInvitationList] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const [smartInvites, setSmartInvites] = useState<string[]>([]);
  const [teamCreationLoadingState, setTeamCreationLoadingState] =
    useState<boolean>(false);

  const { user, isStaff, isLoading } = useContext(AuthContext);

  const [createOrganizationMutation, { error: teamCreationError }] =
    useMutation<
      CreateOrganizationMutationResult,
      CreateOrganizationMutationVariables
    >(CreateOrganizationQuery);

  const [sendOrganizationInvitations] = useMutation<
    SendOrganizationInvitationsMutationResult,
    SendOrganizationInvitationsMutationVariables
  >(SendOrganizationInvitationsQuery);

  const handleBackButtonClick = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      window.history.back();
    }
  };

  const handleTeamCreation = async () => {
    if (window.analytics && user) {
      window.analytics.track('Create a team clicked', {
        isStaff: isStaff(),
        userId: user.id,
        email: user.emailAddress,
        skipInvites: invitationEmailList.length === 0,
      });
    }
    return await createOrganizationMutation({
      variables: {
        name: validatedTeamName,
      },
      onError: (error) => {
        console.error(error);
        throw new Error(error.message);
      },
    });
  };

  const handleSendInvitations = async (organizationId: string) => {
    // Convert all emails to lowercase
    const lowerCaseEmails = invitationEmailList.map((email: string) =>
      email.toLowerCase()
    );

    return await sendOrganizationInvitations({
      variables: {
        addUsersInput: {
          organizationId: `${organizationId}`,
          emails: lowerCaseEmails,
          permissionIds: [],
        },
      },
      // Send segment event on successfully sent invites.
      onCompleted: () => {
        if (window.analytics && user) {
          window.analytics.track(
            'Xometry Organization User Invited To Organization',
            {
              isStaff: isStaff(),
              userId: user.id,
              organizationId,
              emails: lowerCaseEmails.toString(),
              panel: 'Team Creation',
            }
          );
        }
      },
      // Handle catching the error here so that team creation and redirect still occur.
      onError: (error) => {
        console.error(error);
      },
    });
  };

  const handleRedirect = async (
    teamId: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sentInvitationsResponse?: any
  ) => {
    // Adding sentInvitationsResponse update url and send as notification toast to "new team created modal"
    const destination = `/account/organization/${teamId}/settings?create=true`;
    authService.redirectToRefreshToken('www', destination);
  };

  const handleSkipSendButtons = async (buttonType: string) => {
    try {
      setTeamCreationLoadingState(true);
      const teamCreationResp = await handleTeamCreation();
      const teamId = teamCreationResp.data?.createOrganization.refId;
      const organizationId = teamCreationResp.data?.createOrganization.id;
      let sentInvitationsResponse;
      if (
        buttonType === 'sendInvites' &&
        teamId &&
        invitationEmailList.length > 0
      ) {
        sentInvitationsResponse = await handleSendInvitations(organizationId);
      }

      await handleRedirect(teamId, sentInvitationsResponse);
    } catch (e) {
      setTeamCreationLoadingState(false);
      console.error('Team Creation error:', e);
    }
  };

  useEffect(() => {
    const getSmartInvites = async () => {
      if (user?.id) {
        const shareableEntities = await quotesApi.getShareableEntities(user.id);
        const { users = [] } = shareableEntities;
        setSmartInvites([...users]);
      }
    };

    getSmartInvites();
  }, [user]);

  const TeamBenefit = styled.div({
    display: 'flex',
    flexDirection: 'row',
    gap: theme.space[4],
    alignItems: 'center',
  });

  const TeamBenefitIcon = styled.div({
    width: '32px',
    height: '32px',
    color: theme.colors.blues[100],
    backgroundColor: theme.colors.blues[10],
    borderRadius: '100%',
    padding: theme.space[2],
    fontSize: theme.fontSizes[4],
    display: 'flex',
    flex: '0 0 auto',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const TeamBenefitDescription = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.space[1],
  });

  const schema = yup.object().shape({
    teamName: yup
      .string()
      .min(3, 'Minimum 3 characters')
      .max(128, 'Maxiumum 128 characters')
      .test('is-min-length', 'Must be at least 3 characters long', (value) => {
        return (value || '').length >= 3;
      })
      .test(
        'atLeastThreeAlphabetChars',
        'Must contain at least 3 alphabet characters',
        (value) => {
          // Count the number of alphabet characters in the string
          const alphabetCharsCount = (
            (value || '').match(/[A-Za-zÀ-ÖØ-öø-ÿ]/g) || []
          ).length;
          return alphabetCharsCount >= 3;
        }
      ),
  });

  const steps = [
    { id: 'id-1.1', label: 'Create Team', onClick: () => setCurrentStep(1) },
    { id: 'id-1.2', label: 'Send Invitations' },
  ];

  if (isLoading) return <Spinner />;

  return (
    <Flex flexDirection="row">
      {/* Static Sidebar */}
      <Flex flexDirection="column" width="500px">
        <Flex
          flexDirection="column"
          padding={theme.space[6]}
          backgroundColor={theme.colors.darkBlues[5]}
          gap={theme.space[4]}
        >
          <Box>
            <Logo logoType="fullLogo" width="80%" />
          </Box>
          <Text kind="page-title">Teamspace</Text>
          Create a team and add members from your organization to collaborate on
          quotes and orders and get access to Xometry&apos;s Teamspace.{' '}
          <Flex flexDirection="column" gap={theme.space[6]}>
            <TeamBenefit>
              <TeamBenefitIcon>
                <i className="fas fa-file-invoice-dollar" />
              </TeamBenefitIcon>
              <TeamBenefitDescription>
                <Box fontWeight={theme.fontWeights.semibold}>
                  Complete Visibility
                </Box>
                View quotes, orders, and tools in real-time within one unified
                dashboard.
              </TeamBenefitDescription>
            </TeamBenefit>
            <TeamBenefit>
              <TeamBenefitIcon>
                <i className="fa-solid fa-users"></i>
              </TeamBenefitIcon>
              <TeamBenefitDescription>
                <Box fontWeight={theme.fontWeights.semibold}>
                  Project Management
                </Box>
                Review quotes and check the status of each order.
              </TeamBenefitDescription>
            </TeamBenefit>
            <TeamBenefit>
              <TeamBenefitIcon>
                <i className="fa-solid fa-chart-user"></i>
              </TeamBenefitIcon>
              <TeamBenefitDescription>
                <Box fontWeight={theme.fontWeights.semibold}>Collaboration</Box>
                Add colleagues and stakeholders to your team.
              </TeamBenefitDescription>
            </TeamBenefit>
            <TeamBenefit>
              <TeamBenefitIcon>
                <i className="fa-solid fa-truck-fast"></i>
              </TeamBenefitIcon>
              <TeamBenefitDescription>
                <Box fontWeight={theme.fontWeights.semibold}>Data</Box>
                Identify new opportunities for efficiency.
              </TeamBenefitDescription>
            </TeamBenefit>
          </Flex>
        </Flex>
        <Flex justifyContent="center" marginY={theme.space[6]}>
          <img
            src="https://assets.xometry.com/spot-team-discussion.svg"
            alt="Create a Team"
            css={css`
              width: 80%;
            `}
          />
        </Flex>
      </Flex>
      {/* Main content */}
      <Flex
        justifyContent="center"
        width="100%"
        backgroundColor={theme.colors.white}
        height="100vh"
      >
        <Flex flexDirection="column" maxWidth="700px" width="100%">
          {!teamCreationLoadingState && (
            <>
              <Box marginTop={theme.space[8]}>
                <Button
                  kind={theme.kind.primaryOutline}
                  onClick={() => handleBackButtonClick()}
                >
                  <i className="fa fa-arrow-left" />
                  &nbsp;Back
                </Button>
              </Box>
              <Box marginY={theme.space[8]}>
                <Stepper activeStep={currentStep} steps={steps} />
              </Box>
            </>
          )}
          {currentStep === 1 && !teamCreationLoadingState && (
            <>
              <Text kind="subpage-title">Create a Team</Text>
              <Text kind="body" marginTop={theme.space[2]}>
                Team names can be changed at any time from the Team Settings
                page.
              </Text>
              <Formik
                initialValues={{ teamName: validatedTeamName || '' }}
                enableReinitialize
                onSubmit={(values) => {
                  setValidatedTeamName(values.teamName);
                  setCurrentStep(currentStep + 1);
                }}
                validationSchema={schema}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                }) => {
                  return (
                    <form onSubmit={handleSubmit}>
                      <Flex flexDirection="column">
                        <Flex flexDirection="row" marginTop={theme.space[8]}>
                          <Flex flexDirection="column" flex="1 1 auto">
                            <Input
                              id="teamName"
                              name="teamName"
                              value={values.teamName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={Boolean(
                                touched.teamName && errors.teamName
                              )}
                              label={
                                <Flex alignItems="baseline">
                                  <Text kind="label">Team Name</Text>
                                  <Box>
                                    <TooltipBlueIcon placement="top">
                                      Team names can only be between 3 and 128
                                      characters in length and must contain at
                                      least 3 letters (a-z or A-Z).
                                    </TooltipBlueIcon>
                                  </Box>
                                </Flex>
                              }
                            />
                          </Flex>
                          <Flex alignItems="flex-end" flex="0 0 auto">
                            <Box>
                              <Button
                                kind="primary"
                                marginLeft={theme.space[2]}
                                type="submit"
                              >
                                Create Team
                              </Button>
                            </Box>
                          </Flex>
                        </Flex>
                        {Boolean(touched.teamName && errors.teamName) && (
                          <FormMessage invalid>{errors.teamName}</FormMessage>
                        )}
                      </Flex>
                    </form>
                  );
                }}
              </Formik>
            </>
          )}
          {currentStep === 2 && !teamCreationLoadingState && (
            <>
              <Text kind="subpage-title">
                Invite Team Members to {validatedTeamName}
              </Text>
              <Text kind="body" marginTop={theme.space[2]}>
                Send invitations to your colleagues by entering their email
                address below.
              </Text>
              <Box marginY={theme.space[6]}>
                <Flex flexDirection="column" flex="1 1 auto">
                  <EmailSelectCreatable
                    suggestions={smartInvites}
                    label="Add Members"
                    setSelectedEmails={setInvitationEmailList}
                    setAreEmailsValid={setIsValidInvitationList}
                  />
                </Flex>
              </Box>
              <Flex
                flexDirection="row"
                gap={theme.space[2]}
                justifyContent="end"
              >
                <Button
                  kind={theme.kind.primaryOutline}
                  name="skip"
                  onClick={(e) =>
                    handleSkipSendButtons((e.target as HTMLButtonElement).name)
                  }
                >
                  Skip
                </Button>
                <Button
                  kind={theme.kind.primary}
                  disabled={!isValidInvitationList}
                  name="sendInvites"
                  onClick={(e) =>
                    handleSkipSendButtons((e.target as HTMLButtonElement).name)
                  }
                >
                  Send Invitations
                </Button>
              </Flex>
              {teamCreationError && (
                <Alert
                  marginTop={theme.space[4]}
                  isOpen
                  kind={theme.kind.danger}
                >
                  Error creating a team. Please try again.
                </Alert>
              )}
            </>
          )}
          {teamCreationLoadingState && (
            <Flex flexDirection="column" gap="60px" marginTop="90px">
              <Spinner scale="full-screen" height="180px" />
              <Flex
                flexDirection="column"
                alignItems="center"
                gap={theme.space[5]}
                textAlign="center"
              >
                <Text kind="page-title">Creating Your Team </Text>
                <Text kind="body" maxWidth="525px">
                  Your team is being created. This may take a few moments, do
                  not close the window or refresh the page.
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default CreateOrganization;
